import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { useToast } from '../hooks/use-toast';
import { 
  ArrowLeft, MapPin, Calendar, User, Building, FileText, Edit, 
  Plus, Camera, Sparkles, AlertTriangle, CheckCircle, Loader2,
  MessageSquare, Lightbulb, Shield, TrendingUp, Image as ImageIcon,
  X, ChevronDown, ChevronRight, Eye
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EnhancedAuditDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  // Basic audit state
  const [audit, setAudit] = useState(null);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  
  // Issue tracking state
  const [issues, setIssues] = useState([]);
  const [showAddIssue, setShowAddIssue] = useState(false);
  const [currentIssue, setCurrentIssue] = useState({
    title: '',
    description: '',
    location: '',
    severity: 'medium',
    category: '',
    instances: []
  });
  
  // AI Analysis state
  const [aiAnalyzing, setAiAnalyzing] = useState(false);
  const [aiQuestions, setAiQuestions] = useState([]);
  const [aiAnswers, setAiAnswers] = useState({});
  const [aiSolutions, setAiSolutions] = useState(null);
  const [expandedIssue, setExpandedIssue] = useState(null);
  
  // Instance tracking
  const [showAddInstance, setShowAddInstance] = useState(false);
  const [currentInstance, setCurrentInstance] = useState({
    date: new Date().toISOString().split('T')[0],
    time: new Date().toTimeString().split(' ')[0].substring(0,5),
    description: '',
    photo: null,
    witnesses: '',
    immediateAction: ''
  });

  useEffect(() => {
    fetchAuditDetails();
  }, [id]);

  const fetchAuditDetails = async () => {
    try {
      const auditResponse = await axios.get(`${API}/audits/${id}`);
      const auditData = auditResponse.data;
      setAudit(auditData);
      setIssues(auditData.findings || []);
      
      if (auditData.company_id) {
        const companiesResponse = await axios.get(`${API}/companies`);
        const companyData = companiesResponse.data.find(c => c.id === auditData.company_id);
        setCompany(companyData);
      }
    } catch (error) {
      console.error('Error loading audit:', error);
      toast({
        title: "Error",
        description: "Failed to load audit details",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (newStatus) => {
    setUpdating(true);
    try {
      await axios.put(`${API}/audits/${id}`, { status: newStatus });
      setAudit({ ...audit, status: newStatus });
      toast({
        title: "Success",
        description: "Audit status updated successfully"
      });
    } catch (error) {
      console.error('Error updating status:', error);
      toast({
        title: "Error",
        description: "Failed to update audit status",
        variant: "destructive"
      });
    } finally {
      setUpdating(false);
    }
  };

  // Add instance to current issue
  const handleAddInstance = () => {
    const newInstance = {
      id: Date.now().toString(),
      ...currentInstance,
      timestamp: new Date().toISOString()
    };
    
    setCurrentIssue({
      ...currentIssue,
      instances: [...currentIssue.instances, newInstance]
    });
    
    setCurrentInstance({
      date: new Date().toISOString().split('T')[0],
      time: new Date().toTimeString().split(' ')[0].substring(0,5),
      description: '',
      photo: null,
      witnesses: '',
      immediateAction: ''
    });
    
    setShowAddInstance(false);
    
    toast({
      title: "Instance Added",
      description: "Issue instance captured successfully"
    });
  };

  // AI: Analyze issue and generate questions
  const handleAIAnalysis = async (issue) => {
    setAiAnalyzing(true);
    setExpandedIssue(issue.id);
    
    try {
      const response = await axios.post(`${API}/audit/ai-analyze-issue`, {
        issue: {
          title: issue.title,
          description: issue.description,
          severity: issue.severity,
          category: issue.category,
          instances: issue.instances
        }
      });
      
      setAiQuestions(response.data.questions);
      
      toast({
        title: "AI Analysis Complete",
        description: `Generated ${response.data.questions.length} investigative questions`
      });
    } catch (error) {
      console.error('AI analysis error:', error);
      toast({
        title: "Analysis Failed",
        description: "Unable to generate AI analysis",
        variant: "destructive"
      });
    } finally {
      setAiAnalyzing(false);
    }
  };

  // Generate solutions after answering questions
  const handleGenerateSolutions = async (issue) => {
    if (Object.keys(aiAnswers).length === 0) {
      toast({
        title: "Answer Questions First",
        description: "Please answer the AI questions before generating solutions",
        variant: "destructive"
      });
      return;
    }
    
    setAiAnalyzing(true);
    
    try {
      const response = await axios.post(`${API}/audit/ai-generate-solutions`, {
        issue: {
          title: issue.title,
          description: issue.description,
          severity: issue.severity,
          category: issue.category,
          instances: issue.instances
        },
        questions: aiQuestions,
        answers: aiAnswers
      });
      
      setAiSolutions(response.data);
      
      toast({
        title: "Solutions Generated",
        description: "AI has created comprehensive recommendations"
      });
    } catch (error) {
      console.error('Solution generation error:', error);
      toast({
        title: "Generation Failed",
        description: "Unable to generate solutions",
        variant: "destructive"
      });
    } finally {
      setAiAnalyzing(false);
    }
  };

  // Save issue to audit
  const handleSaveIssue = async () => {
    if (!currentIssue.title || !currentIssue.description) {
      toast({
        title: "Missing Information",
        description: "Please provide title and description",
        variant: "destructive"
      });
      return;
    }
    
    const newIssue = {
      id: Date.now().toString(),
      ...currentIssue,
      createdAt: new Date().toISOString()
    };
    
    const updatedIssues = [...issues, newIssue];
    
    try {
      await axios.put(`${API}/audits/${id}`, { findings: updatedIssues });
      setIssues(updatedIssues);
      setShowAddIssue(false);
      setCurrentIssue({
        title: '',
        description: '',
        location: '',
        severity: 'medium',
        category: '',
        instances: []
      });
      
      toast({
        title: "Issue Saved",
        description: "Issue added to audit successfully"
      });
    } catch (error) {
      console.error('Save error:', error);
      toast({
        title: "Save Failed",
        description: "Unable to save issue",
        variant: "destructive"
      });
    }
  };

  const getSeverityColor = (severity) => {
    const colors = {
      critical: 'bg-red-100 text-red-800 border-red-300',
      high: 'bg-orange-100 text-orange-800 border-orange-300',
      medium: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      low: 'bg-blue-100 text-blue-800 border-blue-300'
    };
    return colors[severity] || colors.medium;
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-800',
      in_progress: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      approved: 'bg-purple-100 text-purple-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!audit) {
    return (
      <div className="text-center py-12">
        <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Audit not found</h3>
        <Button onClick={() => navigate('/audits')} variant="outline">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Audits
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/audits')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{audit.name}</h1>
            <p className="text-gray-600">{company?.name || 'Company'}</p>
          </div>
        </div>
        <Badge className={getStatusColor(audit.status)}>
          {audit.status.replace('_', ' ').toUpperCase()}
        </Badge>
      </div>

      {/* Audit Info Card */}
      <Card>
        <CardHeader>
          <CardTitle>Audit Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="flex items-center gap-2">
              <Building className="w-4 h-4 text-gray-500" />
              <div>
                <p className="text-sm text-gray-500">Company</p>
                <p className="font-medium">{company?.name || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-gray-500" />
              <div>
                <p className="text-sm text-gray-500">Location</p>
                <p className="font-medium">{audit.site_location || 'Not specified'}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-gray-500" />
              <div>
                <p className="text-sm text-gray-500">Scheduled</p>
                <p className="font-medium">
                  {audit.scheduled_date ? new Date(audit.scheduled_date).toLocaleDateString() : 'Not set'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-gray-500" />
              <div>
                <p className="text-sm text-gray-500">Issues Found</p>
                <p className="font-medium">{issues.length}</p>
              </div>
            </div>
          </div>
          
          {audit.description && (
            <div className="mt-4 pt-4 border-t">
              <p className="text-sm text-gray-600">{audit.description}</p>
            </div>
          )}
          
          <div className="mt-4 pt-4 border-t">
            <Label>Update Status</Label>
            <Select value={audit.status} onValueChange={handleStatusUpdate} disabled={updating}>
              <SelectTrigger className="mt-2">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Main Content Tabs */}
      <Tabs defaultValue="issues" className="space-y-4">
        <TabsList>
          <TabsTrigger value="issues">Issues & Findings</TabsTrigger>
          <TabsTrigger value="overview">Overview</TabsTrigger>
        </TabsList>

        {/* Issues Tab */}
        <TabsContent value="issues" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold">Safety Issues</h2>
            <Button onClick={() => setShowAddIssue(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Capture New Issue
            </Button>
          </div>

          {/* Issues List */}
          {issues.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <AlertTriangle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Issues Captured</h3>
                <p className="text-gray-600 mb-4">Start capturing safety issues and incidents</p>
                <Button onClick={() => setShowAddIssue(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Capture First Issue
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {issues.map((issue) => (
                <Card key={issue.id} className="border-l-4" style={{borderLeftColor: issue.severity === 'critical' ? '#ef4444' : issue.severity === 'high' ? '#f97316' : issue.severity === 'medium' ? '#eab308' : '#3b82f6'}}>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <CardTitle className="text-xl">{issue.title}</CardTitle>
                          <Badge className={getSeverityColor(issue.severity)}>
                            {issue.severity.toUpperCase()}
                          </Badge>
                          {issue.category && (
                            <Badge variant="outline">{issue.category}</Badge>
                          )}
                        </div>
                        <CardDescription>{issue.description}</CardDescription>
                        {issue.location && (
                          <p className="text-sm text-gray-600 mt-2 flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {issue.location}
                          </p>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setExpandedIssue(expandedIssue === issue.id ? null : issue.id)}
                      >
                        {expandedIssue === issue.id ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      </Button>
                    </div>
                  </CardHeader>

                  {expandedIssue === issue.id && (
                    <CardContent className="space-y-4 border-t pt-4">
                      {/* Instances */}
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-semibold flex items-center gap-2">
                            <FileText className="w-4 h-4" />
                            Captured Instances ({issue.instances?.length || 0})
                          </h4>
                        </div>
                        
                        {issue.instances && issue.instances.length > 0 ? (
                          <div className="space-y-3">
                            {issue.instances.map((instance, idx) => (
                              <div key={instance.id} className="bg-gray-50 rounded-lg p-4 border">
                                <div className="flex items-start justify-between mb-2">
                                  <div className="flex items-center gap-2">
                                    <Badge variant="outline">Instance #{idx + 1}</Badge>
                                    <span className="text-sm text-gray-600">
                                      {instance.date} at {instance.time}
                                    </span>
                                  </div>
                                </div>
                                <p className="text-sm text-gray-700 mb-2">{instance.description}</p>
                                {instance.witnesses && (
                                  <p className="text-sm text-gray-600">
                                    <strong>Witnesses:</strong> {instance.witnesses}
                                  </p>
                                )}
                                {instance.immediateAction && (
                                  <p className="text-sm text-gray-600">
                                    <strong>Immediate Action:</strong> {instance.immediateAction}
                                  </p>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500 italic">No instances captured yet</p>
                        )}
                      </div>

                      {/* AI Analysis Section */}
                      <div className="border-t pt-4">
                        <div className="flex items-center gap-2 mb-3">
                          <Sparkles className="w-5 h-5 text-purple-600" />
                          <h4 className="font-semibold">AI-Powered Investigation</h4>
                        </div>
                        
                        {aiQuestions.length === 0 ? (
                          <Button 
                            onClick={() => handleAIAnalysis(issue)}
                            disabled={aiAnalyzing}
                            className="w-full"
                          >
                            {aiAnalyzing ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Analyzing...
                              </>
                            ) : (
                              <>
                                <Sparkles className="w-4 h-4 mr-2" />
                                Start AI Root Cause Analysis
                              </>
                            )}
                          </Button>
                        ) : (
                          <div className="space-y-4">
                            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                              <h5 className="font-semibold text-purple-900 mb-3">Investigative Questions</h5>
                              <div className="space-y-3">
                                {aiQuestions.map((question, idx) => (
                                  <div key={idx} className="bg-white rounded-lg p-3 border">
                                    <Label className="text-sm font-medium mb-2 block">
                                      {idx + 1}. {question}
                                    </Label>
                                    <Textarea
                                      placeholder="Your answer..."
                                      value={aiAnswers[idx] || ''}
                                      onChange={(e) => setAiAnswers({...aiAnswers, [idx]: e.target.value})}
                                      rows={2}
                                    />
                                  </div>
                                ))}
                              </div>
                              <Button 
                                onClick={() => handleGenerateSolutions(issue)}
                                disabled={aiAnalyzing}
                                className="w-full mt-4"
                              >
                                {aiAnalyzing ? (
                                  <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Generating Solutions...
                                  </>
                                ) : (
                                  <>
                                    <Lightbulb className="w-4 h-4 mr-2" />
                                    Generate Solutions & Recommendations
                                  </>
                                )}
                              </Button>
                            </div>

                            {/* Solutions Display */}
                            {aiSolutions && (
                              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                <h5 className="font-semibold text-green-900 mb-3 flex items-center gap-2">
                                  <CheckCircle className="w-5 h-5" />
                                  AI-Generated Solutions
                                </h5>
                                
                                {/* Summary */}
                                <div className="mb-4">
                                  <h6 className="font-semibold text-sm text-gray-900 mb-2">Issue Summary</h6>
                                  <p className="text-sm text-gray-700 bg-white rounded p-3 border">
                                    {aiSolutions.summary}
                                  </p>
                                </div>

                                {/* Root Causes */}
                                <div className="mb-4">
                                  <h6 className="font-semibold text-sm text-gray-900 mb-2">Root Causes Identified</h6>
                                  <ul className="space-y-2">
                                    {aiSolutions.rootCauses.map((cause, idx) => (
                                      <li key={idx} className="text-sm text-gray-700 bg-white rounded p-3 border flex items-start gap-2">
                                        <span className="font-semibold text-red-600">{idx + 1}.</span>
                                        {cause}
                                      </li>
                                    ))}
                                  </ul>
                                </div>

                                {/* Solutions */}
                                <div className="mb-4">
                                  <h6 className="font-semibold text-sm text-gray-900 mb-2">
                                    <Shield className="w-4 h-4 inline mr-1" />
                                    Recommended Solutions
                                  </h6>
                                  <div className="space-y-3">
                                    {aiSolutions.solutions.map((solution, idx) => (
                                      <div key={idx} className="bg-white rounded p-3 border">
                                        <p className="font-semibold text-sm text-blue-900 mb-1">
                                          Solution {idx + 1}: {solution.title}
                                        </p>
                                        <p className="text-sm text-gray-700">{solution.description}</p>
                                        <div className="mt-2 flex items-center gap-2">
                                          <Badge variant="outline" className="text-xs">
                                            {solution.timeframe}
                                          </Badge>
                                          <Badge variant="outline" className="text-xs">
                                            Cost: {solution.cost}
                                          </Badge>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>

                                {/* Prevention Strategies */}
                                <div>
                                  <h6 className="font-semibold text-sm text-gray-900 mb-2">
                                    <TrendingUp className="w-4 h-4 inline mr-1" />
                                    Prevention & Mitigation Strategies
                                  </h6>
                                  <ul className="space-y-2">
                                    {aiSolutions.preventionStrategies.map((strategy, idx) => (
                                      <li key={idx} className="text-sm text-gray-700 bg-white rounded p-3 border flex items-start gap-2">
                                        <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                                        {strategy}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </CardContent>
                  )}
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Overview Tab */}
        <TabsContent value="overview">
          <Card>
            <CardHeader>
              <CardTitle>Audit Overview</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center p-4 bg-red-50 rounded-lg">
                  <p className="text-3xl font-bold text-red-600">
                    {issues.filter(i => i.severity === 'critical' || i.severity === 'high').length}
                  </p>
                  <p className="text-sm text-gray-600 mt-1">Critical/High Issues</p>
                </div>
                <div className="text-center p-4 bg-yellow-50 rounded-lg">
                  <p className="text-3xl font-bold text-yellow-600">
                    {issues.filter(i => i.severity === 'medium').length}
                  </p>
                  <p className="text-sm text-gray-600 mt-1">Medium Issues</p>
                </div>
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <p className="text-3xl font-bold text-blue-600">
                    {issues.filter(i => i.severity === 'low').length}
                  </p>
                  <p className="text-sm text-gray-600 mt-1">Low Issues</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Issue Dialog */}
      <Dialog open={showAddIssue} onOpenChange={setShowAddIssue}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Capture Safety Issue</DialogTitle>
            <DialogDescription>
              Document a safety issue with multiple instances and AI-powered analysis
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label>Issue Title *</Label>
              <Input
                placeholder="e.g., Missing PPE, Improper Scaffolding, Chemical Spill"
                value={currentIssue.title}
                onChange={(e) => setCurrentIssue({...currentIssue, title: e.target.value})}
              />
            </div>

            <div>
              <Label>Description *</Label>
              <Textarea
                placeholder="Describe the safety issue in detail..."
                value={currentIssue.description}
                onChange={(e) => setCurrentIssue({...currentIssue, description: e.target.value})}
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Severity Level</Label>
                <Select value={currentIssue.severity} onValueChange={(val) => setCurrentIssue({...currentIssue, severity: val})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Category</Label>
                <Input
                  placeholder="e.g., PPE, Equipment, Environment"
                  value={currentIssue.category}
                  onChange={(e) => setCurrentIssue({...currentIssue, category: e.target.value})}
                />
              </div>
            </div>

            <div>
              <Label>Location</Label>
              <Input
                placeholder="Specific location where issue was found"
                value={currentIssue.location}
                onChange={(e) => setCurrentIssue({...currentIssue, location: e.target.value})}
              />
            </div>

            {/* Instances Section */}
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-3">
                <Label className="text-base font-semibold">Issue Instances ({currentIssue.instances.length})</Label>
                <Button variant="outline" size="sm" onClick={() => setShowAddInstance(true)}>
                  <Plus className="w-4 h-4 mr-1" />
                  Add Instance
                </Button>
              </div>

              {currentIssue.instances.length > 0 && (
                <div className="space-y-2 mb-3">
                  {currentIssue.instances.map((instance, idx) => (
                    <div key={instance.id} className="bg-gray-50 rounded p-3 border text-sm">
                      <div className="flex items-center justify-between mb-1">
                        <Badge variant="outline">Instance #{idx + 1}</Badge>
                        <span className="text-gray-600">{instance.date} at {instance.time}</span>
                      </div>
                      <p className="text-gray-700">{instance.description}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex gap-2 pt-4 border-t">
              <Button onClick={handleSaveIssue} className="flex-1">
                <CheckCircle className="w-4 h-4 mr-2" />
                Save Issue to Audit
              </Button>
              <Button variant="outline" onClick={() => setShowAddIssue(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Instance Dialog */}
      <Dialog open={showAddInstance} onOpenChange={setShowAddInstance}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Issue Instance</DialogTitle>
            <DialogDescription>
              Capture details of when and where this issue occurred
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date</Label>
                <Input
                  type="date"
                  value={currentInstance.date}
                  onChange={(e) => setCurrentInstance({...currentInstance, date: e.target.value})}
                />
              </div>
              <div>
                <Label>Time</Label>
                <Input
                  type="time"
                  value={currentInstance.time}
                  onChange={(e) => setCurrentInstance({...currentInstance, time: e.target.value})}
                />
              </div>
            </div>

            <div>
              <Label>Description</Label>
              <Textarea
                placeholder="What happened in this instance?"
                value={currentInstance.description}
                onChange={(e) => setCurrentInstance({...currentInstance, description: e.target.value})}
                rows={3}
              />
            </div>

            <div>
              <Label>Witnesses</Label>
              <Input
                placeholder="Names of people who witnessed this"
                value={currentInstance.witnesses}
                onChange={(e) => setCurrentInstance({...currentInstance, witnesses: e.target.value})}
              />
            </div>

            <div>
              <Label>Immediate Action Taken</Label>
              <Textarea
                placeholder="What was done immediately after discovering this?"
                value={currentInstance.immediateAction}
                onChange={(e) => setCurrentInstance({...currentInstance, immediateAction: e.target.value})}
                rows={2}
              />
            </div>

            <div className="flex gap-2 pt-4">
              <Button onClick={handleAddInstance} className="flex-1">
                <Plus className="w-4 h-4 mr-2" />
                Add Instance
              </Button>
              <Button variant="outline" onClick={() => setShowAddInstance(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EnhancedAuditDetail;
